/**
 * Depreciation schedules, matching Excel's SLN, DB, DDB, SYD (and VDB's switch to
 * straight-line) for whole periods.
 *
 * - SLN  = (cost − salvage) ÷ life
 * - SYD  = (cost − salvage) × (life − period + 1) × 2 ÷ (life × (life + 1))
 * - DDB  = min((cost − prior) × factor ÷ life, cost − salvage − prior)   (factor 2 = double)
 * - DB   = (cost − prior) × rate, with rate = 1 − (salvage ÷ cost)^(1 ÷ life) rounded to
 *          three decimals; the first period is prorated by month ÷ 12 and, when month < 12,
 *          a last period (life + 1) takes (cost − prior) × rate × (12 − month) ÷ 12
 * - Units of production = (cost − salvage) ÷ total units × units used in the period
 *
 * "prior" is the total depreciation of earlier periods.
 */

export type DepreciationMethod =
  | "straight-line"
  | "declining-balance"
  | "double-declining"
  | "sum-of-years"
  | "units";

export const DEPRECIATION_METHODS: readonly DepreciationMethod[] = [
  "straight-line",
  "declining-balance",
  "double-declining",
  "sum-of-years",
  "units",
];

export interface DepreciationRow {
  period: number;
  expense: number;
  accumulated: number;
  bookValue: number;
}

export const MAX_LIFE = 60;

/** Rows from a list of period expenses, tracking accumulated depreciation and book value. */
export function rowsFromExpenses(
  cost: number,
  expenses: readonly number[]
): DepreciationRow[] {
  let accumulated = 0;
  return expenses.map((expense, index) => {
    accumulated += expense;
    return {
      period: index + 1,
      expense,
      accumulated,
      bookValue: cost - accumulated,
    };
  });
}

/** Excel SLN. */
export function sln(cost: number, salvage: number, life: number): number {
  return (cost - salvage) / life;
}

/** Excel SYD: depreciation for `period` (1-based). */
export function syd(
  cost: number,
  salvage: number,
  life: number,
  period: number
): number {
  return ((cost - salvage) * (life - period + 1) * 2) / (life * (life + 1));
}

/** Excel's DB rate: 1 − (salvage ÷ cost)^(1 ÷ life), rounded to three decimals. */
export function dbRate(cost: number, salvage: number, life: number): number {
  const raw = 1 - (salvage / cost) ** (1 / life);
  return Math.round(raw * 1000 + 1e-9) / 1000;
}

/** Excel DB for every period, including the extra last period when `month` < 12. */
export function dbExpenses(
  cost: number,
  salvage: number,
  life: number,
  month = 12
): number[] {
  const rate = dbRate(cost, salvage, life);
  const expenses: number[] = [];
  let prior = 0;
  const periods = month < 12 ? life + 1 : life;
  for (let period = 1; period <= periods; period += 1) {
    let amount = (cost - prior) * rate;
    if (period === 1) {
      amount = (cost * rate * month) / 12;
    } else if (period === life + 1) {
      amount = ((cost - prior) * rate * (12 - month)) / 12;
    }
    expenses.push(amount);
    prior += amount;
  }
  return expenses;
}

/**
 * Excel DDB for every period. With `switchToStraightLine` it also takes the larger of
 * the declining amount and the straight-line share of what is left (Excel VDB, no_switch
 * = FALSE).
 */
export function ddbExpenses(
  cost: number,
  salvage: number,
  life: number,
  factor = 2,
  switchToStraightLine = false
): number[] {
  const expenses: number[] = [];
  let prior = 0;
  for (let period = 1; period <= life; period += 1) {
    const book = cost - prior;
    let amount = Math.min(book * (factor / life), book - salvage);
    if (switchToStraightLine) {
      amount = Math.max(amount, (book - salvage) / (life - period + 1));
    }
    amount = Math.max(0, amount);
    expenses.push(amount);
    prior += amount;
  }
  return expenses;
}

export function straightLineExpenses(
  cost: number,
  salvage: number,
  life: number
): number[] {
  return Array.from({ length: life }, () => sln(cost, salvage, life));
}

export function sydExpenses(
  cost: number,
  salvage: number,
  life: number
): number[] {
  return Array.from({ length: life }, (_, i) =>
    syd(cost, salvage, life, i + 1)
  );
}

/**
 * Units-of-production expenses. Depreciation never takes the book value below salvage,
 * so any units beyond the estimated total add nothing.
 */
export function unitsExpenses(
  cost: number,
  salvage: number,
  totalUnits: number,
  used: readonly number[]
): number[] {
  const perUnit = (cost - salvage) / totalUnits;
  const expenses: number[] = [];
  let prior = 0;
  for (const units of used) {
    const amount = Math.max(
      0,
      Math.min(perUnit * units, cost - salvage - prior)
    );
    expenses.push(amount);
    prior += amount;
  }
  return expenses;
}

export interface CompareOptions {
  /** Months of depreciation in the first year, for DB (default 12). */
  month?: number;
  /** DDB factor (default 2). */
  factor?: number;
  /** DDB switches to straight-line when that is larger. */
  switchToStraightLine?: boolean;
}

export interface CompareRow {
  period: number;
  straightLine: number;
  declining: number;
  double: number;
  sumOfYears: number;
}

/**
 * Expense under the four time-based methods, side by side. DB can run to `life + 1`
 * periods (partial first year); the other methods show 0 in that extra period.
 */
export function compareMethods(
  cost: number,
  salvage: number,
  life: number,
  options: CompareOptions = {}
): CompareRow[] {
  const sl = straightLineExpenses(cost, salvage, life);
  const db = dbExpenses(cost, salvage, life, options.month ?? 12);
  const dd = ddbExpenses(
    cost,
    salvage,
    life,
    options.factor ?? 2,
    options.switchToStraightLine ?? false
  );
  const sy = sydExpenses(cost, salvage, life);
  return db.map((declining, i) => ({
    period: i + 1,
    straightLine: sl[i] ?? 0,
    declining,
    double: dd[i] ?? 0,
    sumOfYears: sy[i] ?? 0,
  }));
}
